import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, AlertTriangle, ShieldCheck, RefreshCw, Download, Filter } from 'lucide-react';
import { stockAPI, branchAPI } from '../../services/api';
import { toast } from 'sonner';

const StockSanityReport = () => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [branches, setBranches] = useState([]);
  const [branchFilter, setBranchFilter] = useState('');
  const [onlyIssues, setOnlyIssues] = useState(true);

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      const params = { only_issues: onlyIssues };
      if (branchFilter) params.branch = branchFilter;
      const data = await stockAPI.stockSanity(params);
      setItems(data.items || []);
      setSummary(data.summary || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load stock sanity report');
      setItems([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [branchFilter, onlyIssues]);

  useEffect(() => {
    branchAPI.getAll().then(setBranches).catch(() => setBranches([]));
  }, []);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const handleExport = () => {
    if (!items.length) {
      toast.error('Nothing to export');
      return;
    }
    const headers = ['Branch', 'SKU', 'Product', 'Category', 'Unit', 'Naive Balance', 'Movement Physical',
                     'Drift', 'Drift Value (₹)', 'Reserved (Awaiting Invoice)', 'Available',
                     'Negative?', 'Drift?', 'Over-committed?'];
    const rows = items.map(r => [
      `"${(r.branch_name || '').replace(/"/g, '""')}"`,
      `"${(r.sku || '').replace(/"/g, '""')}"`,
      `"${(r.product_name || '').replace(/"/g, '""')}"`,
      `"${(r.category || '').replace(/"/g, '""')}"`,
      `"${(r.unit || '').replace(/"/g, '""')}"`,
      r.naive_balance,
      r.movement_physical,
      r.drift,
      r.drift_value,
      r.reserved_awaiting_invoice,
      r.available,
      r.issues?.negative_stock ? 'Yes' : '',
      r.issues?.drift ? 'Yes' : '',
      r.issues?.over_committed ? 'Yes' : '',
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Stock_Sanity_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${items.length} rows`);
  };

  return (
    <div className="space-y-4" data-testid="stock-sanity-report">
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <p className="text-[10px] font-medium text-gray-500 uppercase">Pairs Scanned</p>
            <p className="text-xl font-bold text-gray-900 mt-1">{summary.total_pairs}</p>
          </div>
          <div className={`bg-white rounded-xl border p-4 ${summary.negative_count > 0 ? 'border-red-200 bg-red-50/50' : 'border-gray-100'}`}>
            <p className="text-[10px] font-medium text-gray-500 uppercase">Negative Stock</p>
            <p className={`text-xl font-bold mt-1 ${summary.negative_count > 0 ? 'text-red-600' : 'text-gray-900'}`}>{summary.negative_count}</p>
          </div>
          <div className={`bg-white rounded-xl border p-4 ${summary.over_committed_count > 0 ? 'border-orange-200 bg-orange-50/50' : 'border-gray-100'}`}>
            <p className="text-[10px] font-medium text-gray-500 uppercase">Over-Committed</p>
            <p className={`text-xl font-bold mt-1 ${summary.over_committed_count > 0 ? 'text-orange-600' : 'text-gray-900'}`}>{summary.over_committed_count}</p>
          </div>
          <div className={`bg-white rounded-xl border p-4 ${summary.drift_count > 0 ? 'border-amber-200 bg-amber-50/50' : 'border-gray-100'}`}>
            <p className="text-[10px] font-medium text-gray-500 uppercase">Drift Pairs</p>
            <p className={`text-xl font-bold mt-1 ${summary.drift_count > 0 ? 'text-amber-700' : 'text-gray-900'}`}>{summary.drift_count}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <p className="text-[10px] font-medium text-gray-500 uppercase">Drift Value</p>
            <p className={`text-xl font-bold mt-1 ${summary.total_drift_value !== 0 ? 'text-amber-700' : 'text-gray-900'}`}>
              ₹{(summary.total_drift_value || 0).toLocaleString('en-IN')}
            </p>
          </div>
        </div>
      )}

      {/* Help banner */}
      <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-100 text-[11px] text-blue-800">
        <ShieldCheck className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
        <div>
          <p className="font-semibold">How this report works</p>
          <p className="text-blue-700">
            Compares the legacy <strong>Naive Balance</strong> (Σ <em>stock_items</em> − Σ non-cancelled / non-Awaiting-Invoice dispatches)
            with the unified <strong>Movement Physical</strong> (used by Item History &amp; RTD gate).
            Mismatches mean a stock write was missed somewhere — typically Production FG or STN-in. Negative values mean a
            silent over-deduct slipped through.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Filter className="w-3.5 h-3.5 text-gray-400" />
          <select
            data-testid="sanity-branch-filter"
            value={branchFilter}
            onChange={e => setBranchFilter(e.target.value)}
            className="px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200"
          >
            <option value="">All Branches</option>
            {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
        <label className="flex items-center gap-1.5 text-[11px] text-gray-600 cursor-pointer">
          <input
            data-testid="sanity-only-issues-toggle"
            type="checkbox"
            checked={onlyIssues}
            onChange={e => setOnlyIssues(e.target.checked)}
            className="rounded"
          />
          Show only issues
        </label>
        <button
          data-testid="sanity-refresh-btn"
          onClick={fetchReport}
          className="ml-auto flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
        >
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
        <button
          data-testid="sanity-export-btn"
          onClick={handleExport}
          disabled={!items.length}
          className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-white rounded-lg disabled:opacity-40"
          style={{ backgroundColor: '#24549c' }}
        >
          <Download className="w-3 h-3" /> Export CSV
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-10 text-center">
          <ShieldCheck className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
          <p className="text-sm font-bold text-gray-800">All clean — no sanity issues found</p>
          <p className="text-[11px] text-gray-500 mt-1">Naive and movement-based balances agree across every (product, branch) pair scanned.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="sanity-table">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Branch</th>
                  <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">SKU</th>
                  <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Product</th>
                  <th className="text-right px-2 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Naive</th>
                  <th className="text-right px-2 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Movement</th>
                  <th className="text-right px-2 py-2.5 text-[10px] font-semibold text-amber-700 uppercase">Drift</th>
                  <th className="text-right px-2 py-2.5 text-[10px] font-semibold text-amber-700 uppercase">Drift ₹</th>
                  <th className="text-right px-2 py-2.5 text-[10px] font-semibold text-orange-700 uppercase">Reserved</th>
                  <th className="text-right px-2 py-2.5 text-[10px] font-semibold text-emerald-700 uppercase">Available</th>
                  <th className="text-center px-2 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Issues</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {items.map((r, idx) => (
                  <tr key={idx} className="hover:bg-gray-50/50" data-testid={`sanity-row-${idx}`}>
                    <td className="px-3 py-2 text-gray-700 font-medium">{r.branch_name}</td>
                    <td className="px-3 py-2 font-mono text-[10px] text-gray-600">{r.sku || '-'}</td>
                    <td className="px-3 py-2">
                      <p className="font-medium text-gray-900">{r.product_name}</p>
                      {r.category && <p className="text-[9px] text-gray-400">{r.category}</p>}
                    </td>
                    <td className="px-2 py-2 text-right text-gray-700">{(r.naive_balance ?? 0).toLocaleString('en-IN')} {r.unit}</td>
                    <td className={`px-2 py-2 text-right font-semibold ${r.issues?.negative_stock ? 'text-red-600' : 'text-gray-800'}`}>
                      {(r.movement_physical ?? 0).toLocaleString('en-IN')} {r.unit}
                    </td>
                    <td className={`px-2 py-2 text-right font-bold ${r.issues?.drift ? 'text-amber-700' : 'text-gray-400'}`}>
                      {r.drift > 0 ? '+' : ''}{r.drift?.toLocaleString('en-IN')}
                    </td>
                    <td className={`px-2 py-2 text-right ${r.issues?.drift && r.drift_value !== 0 ? 'text-amber-700 font-semibold' : 'text-gray-400'}`}>
                      ₹{(r.drift_value || 0).toLocaleString('en-IN')}
                    </td>
                    <td className="px-2 py-2 text-right text-orange-700">{(r.reserved_awaiting_invoice ?? 0).toLocaleString('en-IN')}</td>
                    <td className={`px-2 py-2 text-right font-semibold ${r.issues?.over_committed ? 'text-red-600' : 'text-emerald-700'}`}>
                      {(r.available ?? 0).toLocaleString('en-IN')}
                    </td>
                    <td className="px-2 py-2 text-center">
                      <div className="flex items-center justify-center gap-1 flex-wrap">
                        {r.issues?.negative_stock && (
                          <span data-testid={`sanity-flag-negative-${idx}`} className="px-1.5 py-0.5 text-[9px] font-bold rounded-full bg-red-100 text-red-700 flex items-center gap-1" title="Movement-based balance is negative — silent over-deduct">
                            <AlertTriangle className="w-2.5 h-2.5" /> Negative
                          </span>
                        )}
                        {r.issues?.over_committed && (
                          <span data-testid={`sanity-flag-overcommitted-${idx}`} className="px-1.5 py-0.5 text-[9px] font-bold rounded-full bg-orange-100 text-orange-700" title="Reserved (Awaiting Invoice) exceeds physical stock">
                            Over-committed
                          </span>
                        )}
                        {r.issues?.drift && (
                          <span data-testid={`sanity-flag-drift-${idx}`} className="px-1.5 py-0.5 text-[9px] font-bold rounded-full bg-amber-100 text-amber-700" title="Naive vs Movement balances disagree — likely a missed stock write">
                            Drift
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {summary?.generated_at && (
            <div className="px-4 py-2 border-t border-gray-100 text-[10px] text-gray-400">
              Generated {new Date(summary.generated_at).toLocaleString('en-IN')} • {items.length} row{items.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StockSanityReport;
